import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './FactionOverview.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const FactionOverview = ({ userData, hasPermission }) => {
    const [factionsByCat, setFactionsByCat] = useState({}); // { category: [faction, ...] }
    const [categories, setCategories] = useState(['Gang', 'Mafia', 'Staatlich']); // default categories
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Modal states
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [listenCheckModalOpen, setListenCheckModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [dissolveModalOpen, setDissolveModalOpen] = useState(false);
    const [selectedFaction, setSelectedFaction] = useState(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        dc_id: '',
        discord_link: '',
        funk: '',
        routenbesitzer: '',
        standort_plz: '',
        art_der_fraktion: '',
        fraktionsfarbe: '#ffffff',
        sekundare_fraktionsfarbe: '#ffffff',
        category: '' // will be set when editing
    });

    // Listen check form state
    const [listenCheckData, setListenCheckData] = useState({
        anzahl: '',
        datum: new Date().toISOString().split('T')[0]
    });

    // Create form state
    const [createFormData, setCreateFormData] = useState({
        fraktionsname: '',
        art_der_fraktion: '',
        standort_plz: '',
        dauer_testphase: 7,
        dc_id_12er: '',
        discord_link: '',
        funk: '',
        routenbesitzer: '',
        fraktionsfarbe: '#ffffff',
        category: 'Gang' // default category
    });

    // Warning form state
    const [warningFormData, setWarningFormData] = useState({
        grund: ''
    });

    // Dissolve form state
    const [dissolveFormData, setDissolveFormData] = useState({
        grund: ''
    });

    // Track expanded sections (for categories and additional info)
    const [expandedSections, setExpandedSections] = useState({});
    const [collapsedCategories, setCollapsedCategories] = useState({});

    const toggleSection = (factionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [factionId]: !prev[factionId]
        }));
    };

    const toggleCategory = (cat) => {
        setCollapsedCategories(prev => ({
            ...prev,
            [cat]: !prev[cat]
        }));
    };

    const handleAddCategory = () => {
        const newCat = prompt("Name der neuen Kategorie:");
        if (newCat && newCat.trim() !== '') {
            const catName = newCat.trim();
            if (!categories.includes(catName)) {
                setCategories([...categories, catName]);
                setFactionsByCat(prev => ({ ...prev, [catName]: prev[catName] || [] }));
            } else {
                alert("Diese Kategorie existiert bereits!");
            }
        }
    };

    const canManageFactions = hasPermission('view_faction_overview') ||
        userData?.id === '293094387796279297';

    // Function to fetch warning removal logs for a faction
    const fetchWarningLogs = async (factionId) => {
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/factions/${factionId}/warning-logs`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                return response.data.logs;
            } else {
                console.error('Fehler beim Laden der Warnungs-Logs:', response.data.error);
                return [];
            }
        } catch (err) {
            console.error('Fehler beim Laden der Warnungs-Logs:', err);
            return [];
        }
    };
 
    useEffect(() => {
        fetchFactions();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
    // Enhanced fetchFactions to include warning logs and group by category
    const fetchFactions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/factions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                // Fetch warning logs for each faction
                const factionsWithData = await Promise.all(response.data.factions.map(async (faction) => {
                    const logs = await fetchWarningLogs(faction.id);
                    return {
                        ...faction,
                        warning_logs: logs
                    };
                }));
                // Group by category
                const grouped = {};
                factionsWithData.forEach(faction => {
                    const cat = faction.category || 'Unkategorisiert';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(faction);
                });
                // Ensure default categories exist
                const defaultCats = ['Gang', 'Mafia', 'Staatlich'];
                defaultCats.forEach(cat => {
                    if (!grouped[cat]) grouped[cat] = [];
                });
                setFactionsByCat(grouped);

                const foundCats = Object.keys(grouped);
                const newCategories = [...new Set([...defaultCats, ...foundCats])];
                setCategories(newCategories);
            } else {
                setError(response.data.error || 'Fehler beim Laden der Fraktionen');
            }
        } catch (err) {
            console.error('Fehler beim Laden der Fraktionen:', err);
            setError('Fraktionen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };
 
    const handleOnDragEnd = async (result) => {
        const { destination, source, draggableId } = result;
 
        // Dropped outside a list
        if (!destination) {
            return;
        }
 
        // Dropped in the same list and same index
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }
 
        // Get the source and destination lists
        const sourceList = [...factionsByCat[source.droppableId]];
        const destList = [...factionsByCat[destination.droppableId]];
        const [movedItem] = sourceList.splice(source.index, 1);
 
        // Update the moved item's category to the destination category
        movedItem.category = destination.droppableId;
 
        // Insert the item into the destination list
        destList.splice(destination.index, 0, movedItem);
 
        // Update state
        setFactionsByCat(prev => ({
            ...prev,
            [source.droppableId]: sourceList,
            [destination.droppableId]: destList
        }));
 
        // Send update to backend
        try {
            const token = localStorage.getItem('discord_token');
            await axios.put(
                `${API_BASE}/api/factions/${draggableId}/category`,
                { category: destination.droppableId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.error('Fehler beim Aktualisieren der Kategorie:', err);
            // Optionally show a message and revert the change?
            setMessage({ type: 'danger', text: 'Fehler beim Aktualisieren der Kategorie' });
            // Revert the state change (optional)
            setFactionsByCat(prev => ({
                ...prev,
                [source.droppableId]: [...prev[source.droppableId], factionsByCat[destination.droppableId][destination.index]],
                [destination.droppableId]: [
                    ...prev[destination.droppableId].slice(0, destination.index),
                    factionsByCat[source.droppableId][source.index],
                    ...prev[destination.droppableId].slice(destination.index + 1)
                ]
            }));
        }
    };
 
    const formatDate = (dateString) => {
        if (!dateString) return 'Keine Daten';
        return new Date(dateString).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return 'Keine Daten';
        return new Date(dateString).toLocaleDateString('de-DE');
    };

    // Ensure URL has proper protocol for external links and normalize common patterns
    const ensureExternalUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        // Strip surrounding whitespace and angle brackets or parentheses commonly added in chat exports
        let trimmed = url.trim().replace(/^<|>$/g, '').replace(/^\(|\)$/g, '');
        if (!trimmed) return '';
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            return trimmed;
        }
        // Handle discord.gg, invite links, etc.
        if (trimmed.startsWith('discord.gg/') || trimmed.startsWith('discord.com/invite/')) {
            return `https://${trimmed}`;
        }
        // Add https:// prefix
        return `https://${trimmed}`;
    };

    const openEditModal = (faction) => {
        setSelectedFaction(faction);
        setEditFormData({
            dc_id: faction.dc_id_12er || '',
            discord_link: faction.discord_link || '',
            funk: faction.funk || '',
            routenbesitzer: faction.routenbesitzer || '',
            standort_plz: faction.standort_plz || '',
            art_der_fraktion: faction.art_der_fraktion || '',
            fraktionsfarbe: faction.fraktionsfarbe || '#ffffff',
            sekundare_fraktionsfarbe: faction.sekundare_fraktionsfarbe || '#ffffff'
        });
        setEditModalOpen(true);
    };

    const openListenCheckModal = (faction) => {
        setSelectedFaction(faction);
        setListenCheckData({
            anzahl: '',
            datum: new Date().toISOString().split('T')[0]
        });
        setListenCheckModalOpen(true);
    };

    const openWarningModal = (faction) => {
        setSelectedFaction(faction);
        setWarningFormData({ grund: '' });
        setWarningModalOpen(true);
    };

    const openDissolveModal = (faction) => {
        setSelectedFaction(faction);
        setDissolveFormData({ grund: '' });
        setDissolveModalOpen(true);
    };

    const handleWarningSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.post(
                `${API_BASE}/api/factions/${selectedFaction.id}/warning`,
                warningFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Fraktionswarnung erfolgreich hinzugefügt!' });
                setWarningModalOpen(false);
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Hinzufügen' });
            }
        } catch (err) {
            console.error('Fehler beim Hinzufügen der Warnung:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Hinzufügen der Fraktionswarnung' });
        }
    };

    const handleDeleteWarning = async (factionId, warningId) => {
        // Ask for removal reason
        const reason = prompt('Grund für das Entfernen der Warnung (optional):', '');
        if (reason === null) {
            return; // User cancelled
        }

        if (!window.confirm('Möchten Sie diese Warnung wirklich löschen?')) {
            return;
        }

        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.delete(
                `${API_BASE}/api/factions/${factionId}/warning/${warningId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    data: { removed_reason: reason || 'Kein Grund angegeben' }
                }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Warnung erfolgreich gelöscht und geloggt!' });
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Löschen' });
            }
        } catch (err) {
            console.error('Fehler beim Löschen der Warnung:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Löschen der Warnung' });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.put(
                `${API_BASE}/api/factions/${selectedFaction.id}/update`,
                editFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Fraktionsdaten erfolgreich aktualisiert!' });
                setEditModalOpen(false);
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Aktualisieren' });
            }
        } catch (err) {
            console.error('Fehler beim Aktualisieren:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Aktualisieren der Fraktionsdaten' });
        }
    };

    const handleListenCheckSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.post(
                `${API_BASE}/api/factions/${selectedFaction.id}/listen-check`,
                listenCheckData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Listen Check erfolgreich hinzugefügt!' });
                setListenCheckModalOpen(false);
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Hinzufügen' });
            }
        } catch (err) {
            console.error('Fehler beim Hinzufügen:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Hinzufügen des Listen Checks' });
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.post(
                `${API_BASE}/api/factions`,
                createFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Fraktion erfolgreich erstellt!' });
                setCreateModalOpen(false);
                setCreateFormData({
                    fraktionsname: '',
                    art_der_fraktion: '',
                    standort_plz: '',
                    dauer_testphase: 7,
                    dc_id_12er: '',
                    discord_link: '',
                    funk: '',
                    routenbesitzer: '',
                    fraktionsfarbe: '#ffffff'
                });
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Erstellen' });
            }
        } catch (err) {
            console.error('Fehler beim Erstellen:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Erstellen der Fraktion' });
        }
    };

    const handleToggleOfficial = async (faction) => {
        try {
            const token = localStorage.getItem('discord_token');
            const newStatus = !faction.offiziell;
            const response = await axios.put(
                `${API_BASE}/api/factions/${faction.id}/official`,
                { offiziell: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: newStatus
                        ? `✅ "${faction.fraktionsname}" ist jetzt OFFIZIELL!`
                        : `⚠️ Offiziell-Status von "${faction.fraktionsname}" entfernt`
                });
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Ändern des Status' });
            }
        } catch (err) {
            console.error('Fehler beim Ändern des Offiziell-Status:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Ändern des Offiziell-Status' });
        }
    };

    const handleDissolveSubmit = async (e) => {
        e.preventDefault();
        if (!dissolveFormData.grund || !dissolveFormData.grund.trim()) {
            setMessage({ type: 'danger', text: 'Bitte geben Sie einen Grund für die Auflösung an' });
            return;
        }
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.delete(
                `${API_BASE}/api/factions/${selectedFaction.id}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    data: { grund: dissolveFormData.grund }
                }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: `✅ Fraktion "${selectedFaction.fraktionsname}" wurde erfolgreich aufgelöst!` });
                setDissolveModalOpen(false);
                fetchFactions();
            } else {
                setMessage({ type: 'danger', text: response.data.error || 'Fehler beim Auflösen' });
            }
        } catch (err) {
            console.error('Fehler beim Auflösen der Fraktion:', err);
            setMessage({ type: 'danger', text: 'Fehler beim Auflösen der Fraktion' });
        }
    };

    // Helper component for info rows
    const InfoRow = ({ icon, label, value, warning }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className={`fas fa-${icon}`} style={{
                color: warning ? '#ed4245' : '#72767d',
                width: '16px',
                textAlign: 'center'
            }}></i>
            <div>
                <span style={{ color: '#72767d', fontSize: '0.75em' }}>{label}:</span>
                <span style={{
                    color: warning ? '#ed4245' : '#fff',
                    fontWeight: '500',
                    marginLeft: '4px'
                }}>{value}</span>
            </div>
        </div>
    );

    if (!canManageFactions) {
        return (
            <div className="faction-overview-container">
                <div className="dashboard-main">
                    <div className="hero-panel">
                        <h2>🚫 Zugriff verweigert</h2>
                        <p className="muted">Sie haben keine Berechtigung für die Fraktionsverwaltung.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="faction-overview-container">
            <div className="dashboard-main">
                {/* TITEL */}
                <div className="hero-panel">
                    <h2>⚔️ Fraktionsübersicht</h2>
                    <p className="muted">Verwaltung aller Salerno City - Fraktionen</p>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <button className="btn btn-create-faction" onClick={() => setCreateModalOpen(true)}>
                            <i className="fas fa-plus me-2"></i>Fraktion erstellen
                        </button>
                        <button className="btn btn-create-category" onClick={handleAddCategory} style={{ background: '#4f545c', color: '#fff', padding: '10px 20px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
                            <i className="fas fa-folder-plus me-2"></i>Kategorie hinzufügen
                        </button>
                    </div>
                </div>

                {/* MESSAGE */}
                {message && (
                    <div className={`message-banner ${message.type}`}>
                        <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                        {message.text}
                    </div>
                )}

                {/* LOADING */}
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <h3>Fraktionen werden geladen...</h3>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <div className="error-icon">⚠️</div>
                        <h3>Fehler</h3>
                        <p className="muted">{error}</p>
                        <button className="btn btn-retry" onClick={fetchFactions}><i className="fas fa-redo me-2"></i>Erneut versuchen</button>
                    </div>
                ) : Object.values(factionsByCat).flat().length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">⚔️</div>
                        <h3>Keine Fraktionen gefunden</h3>
                        <p className="muted">Es sind noch keine Fraktionen gespeichert.</p>
                    </div>
                ) : (
                    <div className="info-section">
                        <DragDropContext onDragEnd={handleOnDragEnd}>
                            {categories.map(cat => {
                                const catFactions = factionsByCat[cat] || [];
                                const isCollapsed = collapsedCategories[cat];
                                return (
                                    <div key={cat} className="category-section">
                                        <div className="category-header" onClick={() => toggleCategory(cat)}>
                                            <h4 className="category-title">
                                                <i className="fas fa-folder category-icon"></i> {cat} 
                                                <span className="category-badge">{catFactions.length}</span>
                                            </h4>
                                            <div>
                                                <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} category-toggle-icon`}></i>
                                            </div>
                                        </div>
                                        
                                        {!isCollapsed && (
                                            <Droppable droppableId={cat}>
                                                {(provided, snapshot) => (
                                                    <div 
                                                        {...provided.droppableProps} 
                                                        ref={provided.innerRef}
                                                            style={{ 
                                                                display: 'grid', 
                                                                gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))', 
                                                                gap: '24px',
                                                                padding: '10px',
                                                                minHeight: '100px',
                                                                background: snapshot.isDraggingOver ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                                borderRadius: '8px',
                                                                transition: 'background 0.2s ease'
                                                            }}
                                                    >
                                                        {catFactions.map((faction, index) => {
                                                            const discordUrl = ensureExternalUrl(faction.discord_link);
                                                            return (
                                                                <Draggable key={faction.id.toString()} draggableId={faction.id.toString()} index={index}>
                                                                    {(provided, snapshot) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            style={{
                                                                                ...provided.draggableProps.style,
                                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className="faction-card"
                                                                                style={{ margin: 0 }}
                                                                            >
                                        {/* Card Header with Color Bar */}
                                        <div className="faction-card-header">
                                            <div className="faction-card-title-row">
                                                {faction.fraktionsfarbe && (
                                                    <div 
                                                        className="faction-color-dot"
                                                        style={{ backgroundColor: faction.fraktionsfarbe, boxsalerno: `0 0 10px ${faction.fraktionsfarbe}80` }}
                                                    ></div>
                                                )}
                                                <h5 className="faction-card-title">{faction.fraktionsname}</h5>
                                            </div>
                                            {faction.offiziell && (
                                                <span className="faction-card-badge">
                                                    <i className="fas fa-crown"></i>OFFIZIELL
                                                </span>
                                            )}
                                        </div>

                                        {/* Card Body */}
                                        <div className="faction-card-body">
                                            {/* Quick Info Row */}
                                            <div className="faction-quick-info">
                                                <div className="faction-quick-info-item">
                                                    <div className="faction-quick-info-label">Art</div>
                                                    <div className="faction-quick-info-value">{faction.art_der_fraktion}</div>
                                                </div>
                                                <div className="faction-quick-info-item">
                                                    <div className="faction-quick-info-label">PLZ</div>
                                                    <div className="faction-quick-info-value">{faction.standort_plz || '-'}</div>
                                                </div>
                                                <div className="faction-quick-info-item">
                                                    <div className="faction-quick-info-label">Members</div>
                                                    <div className="faction-quick-info-value">{faction.avg_members_7days ? `${faction.avg_members_7days} ∅` : '-'}</div>
                                                </div>
                                            </div>

                                            {/* Info Grid */}
                                            <div className="faction-info-grid">
                                                <InfoRow icon="calendar-alt" label="Erstellt" value={formatDateShort(faction.created_at)} />
                                                <InfoRow icon="clock" label="Testphase" value={`${faction.dauer_testphase} Tage`} />
                                                <InfoRow icon="exclamation-triangle" label="Warns" value={faction.warn_count || 0} warning={faction.warn_count > 0} />
                                            </div>

                                            {/* 7-Day Average Listen Check */}
                                            <div className="faction-listen-check">
                                                <i className={`fas ${faction.avg_members_7days ? 'fa-chart-line' : 'fa-minus-circle'} faction-listen-check-icon`}></i>
                                                <span className="faction-listen-check-text">
                                                    <strong>7-Tage ∅:</strong> {faction.avg_members_7days
                                                        ? `${faction.avg_members_7days} Members`
                                                        : 'Nicht genügend Daten'}
                                                </span>
                                            </div>

                                            {/* Recent Listen Checks History */}
                                            {faction.recent_listen_checks && faction.recent_listen_checks.length > 0 && (
                                                <div style={{ marginBottom: '16px' }}>
                                                    <button
                                                        className="faction-btn-action"
                                                        type="button"
                                                        onClick={() => toggleSection(`listen-${faction.id}`)}
                                                        style={{ width: '100%' }}
                                                    >
                                                        <i className="fas fa-chart-bar"></i>
                                                        <span>Letzte {faction.recent_listen_checks.length} Checks</span>
                                                        <i className="fas fa-chevron-up" style={{ marginLeft: 'auto' }}></i>
                                                    </button>
                                                    <div
                                                        style={{
                                                            marginTop: '8px',
                                                            overflow: 'hidden',
                                                            transition: 'max-height 0.3s ease, opacity 0.3s ease',
                                                            maxHeight: expandedSections[`listen-${faction.id}`] ? '500px' : '0',
                                                            opacity: expandedSections[`listen-${faction.id}`] ? 1 : 0
                                                        }}
                                                    >
                                                        <div className="faction-listen-history">
                                                            {faction.recent_listen_checks.map((check, idx) => (
                                                                <div key={idx} className="faction-listen-history-item">
                                                                    <span className="faction-listen-history-date">
                                                                        <i className="fas fa-calendar-alt"></i>
                                                                        {formatDateShort(check.datum)}
                                                                    </span>
                                                                    <span className="faction-listen-history-count">
                                                                        <i className="fas fa-users"></i>
                                                                        {check.anzahl}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Discord & Contact Info */}
                                            <div style={{ marginBottom: '16px' }}>
                                                {faction.discord_link ? (
                                                    <a
                                                        href={discordUrl || '#'}
                                                        onClick={(e) => { e.preventDefault(); if (discordUrl) window.open(discordUrl, '_blank', 'noopener'); }}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="faction-discord-link"
                                                    >
                                                        <i className="fab fa-discord"></i>
                                                        <span>Discord beitreten</span>
                                                        <i className="fas fa-external-link-alt ms-auto"></i>
                                                    </a>
                                                ) : (
                                                    <div className="faction-no-discord">
                                                        <i className="fab fa-discord"></i>
                                                        <span>Kein Discord-Link</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Additional Info */}
                                            {(faction.funk || faction.routenbesitzer) && (
                                                <div style={{
                                                    padding: '10px 14px',
                                                    background: 'rgba(255, 255, 255, 0.02)',
                                                    borderRadius: 'var(--card-radius-sm)',
                                                    marginBottom: '16px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    {faction.funk && (
                                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                                                            <i className="fas fa-headset" style={{ color: 'var(--accent-color)' }}></i>
                                                            <span style={{ color: 'var(--text-secondary)' }}>Funk: <strong style={{ color: 'var(--text-primary)' }}>{faction.funk}</strong></span>
                                                        </div>
                                                    )}
                                                    {faction.routenbesitzer && (
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <i className="fas fa-road" style={{ color: 'var(--warning-color)' }}></i>
                                                            <span style={{ color: 'var(--text-secondary)' }}>Routen: <strong style={{ color: 'var(--text-primary)' }}>{faction.routenbesitzer}</strong></span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* WARNINGS */}
                                            {faction.warnings && faction.warnings.length > 0 && (
                                                <div className="faction-warning-section">
                                                    <button
                                                        className="faction-warning-btn"
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target={`#warnDetails-${faction.id}`}
                                                    >
                                                        <i className="fas fa-exclamation-triangle"></i>
                                                        {faction.warnings.length} Warn{faction.warnings.length > 1 ? 's' : ''} anzeigen
                                                    </button>
                                                    <div className="collapse faction-warning-details" id={`warnDetails-${faction.id}`}>
                                                        {faction.warnings.map((warn) => (
                                                            <div key={warn.id} className="faction-warning-item">
                                                                <div className="faction-warning-header">
                                                                    <span className="faction-warning-date">{formatDate(warn.datum)}</span>
                                                                    <button
                                                                        className="faction-warning-delete"
                                                                        onClick={() => handleDeleteWarning(faction.id, warn.id)}
                                                                        title="Warnung löschen"
                                                                    >
                                                                        🗑️
                                                                    </button>
                                                                </div>
                                                                <div className="faction-warning-text">{warn.grund}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* ADDITIONAL INFO SECTION - Only for HeadFraktionsverwaltung */}
                                            {canManageFactions && (
                                                <div className="faction-additional-info">
                                                    <div className="faction-additional-header">
                                                        <h6 className="faction-additional-title">
                                                            <i className="fas fa-info-circle"></i>
                                                            Weitere Infos
                                                        </h6>
                                                        <button
                                                            className="faction-toggle-btn"
                                                            onClick={() => toggleSection(`additional-${faction.id}`)}
                                                        >
                                                            {expandedSections[`additional-${faction.id}`] ? 'Ausblenden' : 'Anzeigen'}
                                                        </button>
                                                    </div>

                                                    <div
                                                        style={{
                                                            maxHeight: expandedSections[`additional-${faction.id}`] ? '500px' : '0',
                                                            overflow: 'hidden',
                                                            transition: 'max-height 0.3s ease',
                                                            opacity: expandedSections[`additional-${faction.id}`] ? 1 : 0
                                                        }}
                                                    >
                                                        {/* Warning Removal Logs */}
                                                        <div style={{ marginBottom: '12px' }}>
                                                            <h6 style={{ margin: '0 0 8px 0', color: 'var(--accent-color)', fontSize: '0.85rem' }}>
                                                                <i className="fas fa-history"></i>
                                                                Warnungs-Entfernungen
                                                            </h6>
                                                            <div style={{
                                                                background: 'rgba(255, 255, 255, 0.02)',
                                                                padding: '8px',
                                                                borderRadius: 'var(--card-radius-sm)',
                                                                fontSize: '0.8rem'
                                                            }}>
                                                                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                                                                    Letzte 5 Entfernungen:
                                                                </div>
                                                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                                                    {faction.warning_logs && faction.warning_logs.length > 0 ? (
                                                                        faction.warning_logs.slice(0, 5).map((log, index) => (
                                                                            <div key={index} style={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                padding: '4px 0',
                                                                                borderBottom: index < faction.warning_logs.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                                                                            }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                    <div style={{
                                                                                        width: '8px',
                                                                                        height: '8px',
                                                                                        borderRadius: '50%',
                                                                                        background: 'var(--danger-color)'
                                                                                    }}></div>
                                                                                    <div>
                                                                                        <div style={{ color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: '500' }}>
                                                                                            Discord User: {log.removed_by_user_id || 'Unbekannt'}
                                                                                        </div>
                                                                                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                                                                                            {formatDate(log.logged_at)}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ textAlign: 'right' }}>
                                                                                    <div style={{ color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: '500' }}>
                                                                                        Grund: {log.removed_reason || 'Kein Grund'}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                                                            Keine Warnungs-Entfernungen vorhanden
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Additional Statistics */}
                                                        <div className="faction-stats-grid">
                                                            <div className="faction-stat-item">
                                                                <div className="faction-stat-label">Erstellt</div>
                                                                <div className="faction-stat-value">{formatDateShort(faction.created_at)}</div>
                                                            </div>
                                                            <div className="faction-stat-item">
                                                                <div className="faction-stat-label">Letzte Warnung</div>
                                                                <div className="faction-stat-value">
                                                                    {faction.warnings && faction.warnings.length > 0
                                                                        ? formatDateShort(faction.warnings[0].datum)
                                                                        : 'Keine'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ACTIONS */}
                                            <div className="faction-actions">
                                                <button
                                                    className="faction-btn-action"
                                                    onClick={() => openEditModal(faction)}
                                                    title="Fraktion bearbeiten"
                                                >
                                                    <i className="fas fa-pen-to-square"></i>
                                                    <span>Bearbeiten</span>
                                                </button>
                                                <button
                                                    className="faction-btn-action success"
                                                    onClick={() => openListenCheckModal(faction)}
                                                    title="Listen Check hinzufügen"
                                                >
                                                    <i className="fas fa-clipboard-list"></i>
                                                    <span>Listen Check</span>
                                                </button>
                                                <button
                                                    className="faction-btn-action warning"
                                                    onClick={() => openWarningModal(faction)}
                                                    title="Fraktionswarnung hinzufügen"
                                                >
                                                    <i className="fas fa-triangle-exclamation"></i>
                                                    <span>Fraktionswarn</span>
                                                </button>
                                            </div>

                                            {/* ADMIN ACTIONS: OFFIZIELL TOGGLE & DISSOLVE */}
                                            {canManageFactions && (
                                                <>
                                                    {!faction.offiziell && (
                                                        <button
                                                            className="faction-btn-official"
                                                            onClick={() => handleToggleOfficial(faction)}
                                                        >
                                                            <i className="fas fa-crown"></i>
                                                            Offizielle Nachricht senden !
                                                        </button>
                                                    )}

                                                    {/* FACTION DISSOLVE BUTTON */}
                                                    <button
                                                        className="faction-btn-dissolve"
                                                        onClick={() => openDissolveModal(faction)}
                                                    >
                                                        <i className="fas fa-gavel"></i>
                                                        Fraktion auflösen
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Draggable>
                    );
                })}
                {provided.placeholder}
            </div>
        )}
    </Droppable>
)}
                                    </div>
                                );
                            })}
                        </DragDropContext>
                    </div>
                )}

                {/* EDIT MODAL */}
                {editModalOpen && selectedFaction && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}>
                        <div className="modal-content" style={{
                            background: '#2f3136',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '15px 20px',
                                borderBottom: '1px solid #40444b',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ margin: 0, color: '#fff' }}>Fraktion bearbeiten: {selectedFaction.fraktionsname}</h5>
                                <button
                                    onClick={() => setEditModalOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body" style={{ padding: '20px' }}>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Discord ID (12er)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.dc_id}
                                            onChange={(e) => setEditFormData({ ...editFormData, dc_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Discord Link</label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.discord_link}
                                            onChange={(e) => setEditFormData({ ...editFormData, discord_link: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Funk</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.funk}
                                            onChange={(e) => setEditFormData({ ...editFormData, funk: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Routenbesitzer</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.routenbesitzer}
                                            onChange={(e) => setEditFormData({ ...editFormData, routenbesitzer: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>PLZ</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.standort_plz}
                                            onChange={(e) => setEditFormData({ ...editFormData, standort_plz: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Art der Fraktion</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={editFormData.art_der_fraktion}
                                            onChange={(e) => setEditFormData({ ...editFormData, art_der_fraktion: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Fraktionsfarbe</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde', height: '40px' }}
                                            value={editFormData.fraktionsfarbe}
                                            onChange={(e) => setEditFormData({ ...editFormData, fraktionsfarbe: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Sekundäre Fraktionsfarbe</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde', height: '40px' }}
                                            value={editFormData.sekundare_fraktionsfarbe}
                                            onChange={(e) => setEditFormData({ ...editFormData, sekundare_fraktionsfarbe: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer" style={{
                                    padding: '15px 20px',
                                    borderTop: '1px solid #40444b',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}>
                                    <button
                                        type="button"
                                        className="btn btn-modal-secondary"
                                        onClick={() => setEditModalOpen(false)}
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="btn btn-modal-primary">
                                        <i className="fas fa-check me-2"></i>Speichern
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* LISTEN CHECK MODAL */}
                {listenCheckModalOpen && selectedFaction && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}>
                        <div className="modal-content" style={{
                            background: '#2f3136',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '15px 20px',
                                borderBottom: '1px solid #40444b',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ margin: 0, color: '#fff' }}>Neuer Listen Check: {selectedFaction.fraktionsname}</h5>
                                <button
                                    onClick={() => setListenCheckModalOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleListenCheckSubmit}>
                                <div className="modal-body" style={{ padding: '20px' }}>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Anzahl</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={listenCheckData.anzahl}
                                            onChange={(e) => setListenCheckData({ ...listenCheckData, anzahl: e.target.value })}
                                            required
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Datum</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={listenCheckData.datum}
                                            onChange={(e) => setListenCheckData({ ...listenCheckData, datum: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer" style={{
                                    padding: '15px 20px',
                                    borderTop: '1px solid #40444b',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}>
                                    <button
                                        type="button"
                                        className="btn btn-modal-secondary"
                                        onClick={() => setListenCheckModalOpen(false)}
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="btn btn-modal-primary">
                                        <i className="fas fa-plus me-2"></i>Hinzufügen
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* CREATE FACTION MODAL */}
                {createModalOpen && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}>
                        <div className="modal-content" style={{
                            background: '#2f3136',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '600px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '15px 20px',
                                borderBottom: '1px solid #40444b',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ margin: 0, color: '#fff' }}>Neue Fraktion erstellen</h5>
                                <button
                                    onClick={() => setCreateModalOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleCreateSubmit}>
                                <div className="modal-body" style={{ padding: '20px' }}>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Fraktionsname *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.fraktionsname}
                                            onChange={(e) => setCreateFormData({ ...createFormData, fraktionsname: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Art der Fraktion *</label>
                                        <select
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.art_der_fraktion}
                                            onChange={(e) => setCreateFormData({ ...createFormData, art_der_fraktion: e.target.value })}
                                            required
                                        >
                                            <option value="">-- Art auswählen --</option>
                                            <option value="Gang">Gang</option>
                                            <option value="Mafia">Mafia</option>
                                            <option value="Kartell">Kartell</option>
                                            <option value="Bruderschaft">Bruderschaft</option>
                                            <option value="MC/Biker">MC / Biker </option>
                                            <option value="Tankstelle">Tankstelle</option>
                                            <option value="Behörde">Behörde</option>
                                            <option value="Rettungsdienst">Rettungsdienst</option>
                                            <option value="Polizei">Polizei</option>
                                            <option value="News">News</option>
                                            <option value="Taxi">Taxi</option>
                                            <option value="Motel">Motel</option>
                                            <option value="Business">Business</option>
                                            <option value="Werkstatt">Werkstatt</option>
                                            <option value="Sonstiges">Sonstiges</option>
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Standort (PLZ)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.standort_plz}
                                            onChange={(e) => setCreateFormData({ ...createFormData, standort_plz: e.target.value })}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div className="form-group">
                                            <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Testphase (Tage)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                                value={createFormData.dauer_testphase}
                                                onChange={(e) => setCreateFormData({ ...createFormData, dauer_testphase: parseInt(e.target.value) || 1 })}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <hr style={{ borderTop: '1px solid #40444b', margin: '20px 0' }} />
                                    <h6 style={{ color: '#fff', marginBottom: '15px' }}>Optionale Daten</h6>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Discord ID (12er)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.dc_id_12er}
                                            onChange={(e) => setCreateFormData({ ...createFormData, dc_id_12er: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Discord Link</label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.discord_link}
                                            onChange={(e) => setCreateFormData({ ...createFormData, discord_link: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Funk</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.funk}
                                            onChange={(e) => setCreateFormData({ ...createFormData, funk: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Routenbesitzer</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde' }}
                                            value={createFormData.routenbesitzer}
                                            onChange={(e) => setCreateFormData({ ...createFormData, routenbesitzer: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Fraktionsfarbe</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde', height: '40px' }}
                                            value={createFormData.fraktionsfarbe}
                                            onChange={(e) => setCreateFormData({ ...createFormData, fraktionsfarbe: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer" style={{
                                    padding: '15px 20px',
                                    borderTop: '1px solid #40444b',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}>
                                    <button
                                        type="button"
                                        className="btn btn-modal-secondary"
                                        onClick={() => setCreateModalOpen(false)}
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="btn btn-modal-success">
                                        <i className="fas fa-plus me-2"></i>Erstellen
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* WARNING MODAL */}
                {warningModalOpen && selectedFaction && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}>
                        <div className="modal-content" style={{
                            background: '#2f3136',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '15px 20px',
                                borderBottom: '1px solid #40444b',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ margin: 0, color: '#fff' }}>Warnung hinzufügen: {selectedFaction.fraktionsname}</h5>
                                <button
                                    onClick={() => setWarningModalOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleWarningSubmit}>
                                <div className="modal-body" style={{ padding: '20px' }}>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Grund der Warnung</label>
                                        <textarea
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde', minHeight: '100px' }}
                                            value={warningFormData.grund}
                                            onChange={(e) => setWarningFormData({ ...warningFormData, grund: e.target.value })}
                                            placeholder="Bitte den Grund für die Warnung eingeben..."
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer" style={{
                                    padding: '15px 20px',
                                    borderTop: '1px solid #40444b',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}>
                                    <button
                                        type="button"
                                        className="btn btn-modal-secondary"
                                        onClick={() => setWarningModalOpen(false)}
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="btn btn-modal-warning">
                                        <i className="fas fa-triangle-exclamation me-2"></i>Warnung hinzufügen
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* DISSOLVE MODAL */}
                {dissolveModalOpen && selectedFaction && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}>
                        <div className="modal-content" style={{
                            background: '#2f3136',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '15px 20px',
                                borderBottom: '1px solid #40444b',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ margin: 0, color: '#fff' }}>Fraktion auflösen: {selectedFaction.fraktionsname}</h5>
                                <button
                                    onClick={() => setDissolveModalOpen(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleDissolveSubmit}>
                                <div className="modal-body" style={{ padding: '20px' }}>
                                    <div style={{
                                        background: '#ed424520',
                                        border: '1px solid #ed4245',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '15px'
                                    }}>
                                        <p style={{ color: '#ed4245', margin: 0, fontSize: '0.9em' }}>
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
                                        </p>
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ color: '#fff', marginBottom: '5px', display: 'block' }}>Grund der Auflösung *</label>
                                        <textarea
                                            className="form-control"
                                            style={{ background: '#40444b', border: '1px solid #5d6168', color: '#dcddde', minHeight: '100px' }}
                                            value={dissolveFormData.grund}
                                            onChange={(e) => setDissolveFormData({ ...dissolveFormData, grund: e.target.value })}
                                            placeholder="Bitte den Grund für die Auflösung der Fraktion eingeben..."
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer" style={{
                                    padding: '15px 20px',
                                    borderTop: '1px solid #40444b',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}>
                                    <button
                                        type="button"
                                        className="btn btn-modal-secondary"
                                        onClick={() => setDissolveModalOpen(false)}
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="btn btn-modal-danger">
                                        <i className="fas fa-gavel me-2"></i>
                                        Fraktion auflösen
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FactionOverview;
